import type { APIClient } from '../../apiClient';
import { workflowRoutes } from '../workflows/workflowRoutes';
import type { CreateWorkflowVersionPayload } from '../workflows/workflowsApi';
import { libraryRoutes } from './libraryRoutes';
import type {
  SnippetMetadata,
  SnippetWithPublishedVersion,
  SnippetVersionDetails,
  SnippetDetails,
  SnippetVersionProps,
} from 'flow/service/snippets';
import type {
  SnippetsResponse,
  SnippetVersionsListResponse,
  CreatedSnippetWorkflowResponse,
  SnippetFiltersResponse,
  SnippetApiFilters,
} from '~/helpers/api-new/modules/library/types';
import { fetchAllPaginatedSnippets } from './helpers';
import isEmpty from 'lodash/isEmpty';

export type LibraryScope = 'global' | 'internal' | 'local';
export type CreateOrUpdateSnippetMetadata = Partial<Omit<SnippetMetadata, 'id' | 'token' | 'integrations' | 'date_created' | 'date_updated'>>;

export type PaginatedRequestParams = {
  pageNum?: number;
  pageSize?: number;
};

export type UpdateSnippetMetadataData = {
  snippet_metadata: CreateOrUpdateSnippetMetadata;
};

type CreateSnippetData = {
  snippet_token: string;
  snippet_metadata: CreateOrUpdateSnippetMetadata;
};

type CreateSnippetWorkflowData = {
  name: string;
  type: string;
  workflow_sku_id: number;
  scope: LibraryScope;
};

export type GetSnippetsParams = PaginatedRequestParams & {
  filters?: SnippetApiFilters;
  sort?: {
    field: string;
    direction: 'ASC' | 'DESC';
  };
  scope?: string;
};

const libraryApiMetaConfig = {
  silent: true,
  skipTransformResponseBody: true,
};

// Query param field mapping: frontend camelCase -> API snake_case (only for query params!)
const queryFieldNameMapping: Record<string, string> = {
  dateCreated: 'date_created',
  dateUpdated: 'date_updated',
  workflowType: 'workflow_type',
};

// Transform frontend filters to API format
function transformFiltersToApi(filters: SnippetApiFilters): SnippetApiFilters {
  const apiFilters: SnippetApiFilters = {};

  Object.entries(filters).forEach(([frontendField, filterValue]) => {
    const apiField = queryFieldNameMapping[frontendField] || frontendField;
    apiFilters[apiField] = filterValue;
  });

  return apiFilters;
}

// Transform frontend sort field to API field
function transformSortFieldToApi(field: string): string {
  return queryFieldNameMapping[field] || field;
}

export default function createLibraryAPI(apiClient: APIClient) {
  const libraryAPI = {
    getSnippets: async (params?: GetSnippetsParams): Promise<SnippetsResponse> => {
      const queryParams = new URLSearchParams();

      if (params?.pageNum) queryParams.append('pageNum', params.pageNum.toString());
      if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());

      const apiFilters = params?.filters ? transformFiltersToApi(params.filters) : {};
      if (params?.scope) {
        apiFilters.scope = { values: [params.scope] };
      }
      if (!isEmpty(apiFilters)) {
        queryParams.append('filters', JSON.stringify(apiFilters));
      }
      if (params?.sort) {
        const apiField = transformSortFieldToApi(params.sort.field);
        queryParams.append('sort', JSON.stringify([[apiField, params.sort.direction]]));
      }

      const url = `${libraryRoutes.snippets}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await apiClient.get<SnippetsResponse>(url, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Get Snippets',
        },
      });

      // Convert date strings to Date objects
      const parsed: SnippetsResponse = {
        ...response,
        snippets: response.snippets.map(s => ({
          ...s,
          id: s.token,
        })),
      };

      return parsed;
    },
    getSnippet: (token: string): Promise<SnippetWithPublishedVersion> => {
      const url = libraryRoutes.snippet(token);

      return apiClient.get(url, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Get Snippet',
        },
      });
    },
    getSnippetVersion: (token: string, version: number): Promise<SnippetVersionDetails> => {
      const url = libraryRoutes.snippetVersion(token, version);

      return apiClient.get(url, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Get Snippet Version',
        },
      });
    },
    getSnippetDetails: async (token: string): Promise<{
      snippetDetails: SnippetDetails;
      snippetVersionProps: SnippetVersionProps;
    }> => {
      const snippetResponse = await libraryAPI.getSnippet(token);
      const publishedVersion = snippetResponse.snippet_metadata.published_version;
      if (publishedVersion === null) {
        throw new Error(`Snippet ${token} has no published version`);
      }
      const versionResponse = await libraryAPI.getSnippetVersion(token, publishedVersion);

      return {
        snippetDetails: {
          snippet_metadata: snippetResponse.snippet_metadata,
          snippet_data: versionResponse.snippet_version_data,
          snippet_version_metadata: versionResponse.snippet_version_metadata,
        },
        snippetVersionProps: versionResponse.snippet_version_props,
      };
    },
    listSnippetVersions: (token: string): Promise<SnippetVersionsListResponse> => {
      const url = libraryRoutes.snippetVersions(token);

      return apiClient.get(url, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'List Snippet Versions',
        },
      });
    },
    updateSnippetMetadata: (token: string, data: UpdateSnippetMetadataData) => {
      const url = libraryRoutes.snippetMetadata(token);

      return apiClient.put(url, data, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Update Snippet Metadata',
        },
      });
    },
    createSnippet: (data: CreateSnippetData) => {
      const url = libraryRoutes.snippets;

      return apiClient.post(url, data, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Create Snippet',
        },
      });
    },
    createSnippetWorkflow: (data: CreateSnippetWorkflowData) => (
      apiClient.post<CreatedSnippetWorkflowResponse>(workflowRoutes.root, {
        ...data,
        snippet_scope: data.scope,
      }, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Create Snippet Workflow',
        },
      })
    ),
    publishSnippet: (token: string, versionNumber: number) => {
      const url = libraryRoutes.publishSnippet(token);

      return apiClient.patch(url, { version: versionNumber }, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Publish Snippet',
        },
      });
    },
    unpublishSnippet: (token: string) => {
      const url = libraryRoutes.unpublishSnippet(token);

      return apiClient.patch(url, {}, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Unpublish Snippet',
        },
      });
    },
    archiveSnippet: (token: string) => {
      const url = libraryRoutes.archiveSnippet(token);

      return apiClient.patch(url, {}, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Archive Snippet',
        },
      });
    },
    unarchiveSnippet: (token: string) => {
      const url = libraryRoutes.unarchiveSnippet(token);

      return apiClient.patch(url, {}, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Unarchive Snippet',
        },
      });
    },
    promoteSnippet: (token: string) => {
      const url = libraryRoutes.promoteSnippet(token);

      return apiClient.patch(url, {}, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Promote Snippet',
        },
      });
    },
    demoteSnippet: (token: string) => {
      const url = libraryRoutes.demoteSnippet(token);

      return apiClient.patch(url, {}, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Demote Snippet',
        },
      });
    },
    getFilters: (): Promise<SnippetFiltersResponse> => {
      const url = libraryRoutes.filters;

      return apiClient.get(url, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Get Snippet Filters',
        },
      });
    },
    getSnippetNames: (): Promise<{ names: string[] }> => {
      const url = libraryRoutes.snippetNames;

      return apiClient.get(url, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Get Snippet Names',
        },
      });
    },

    // Local snippets
    createLocalSnippet: (data: CreateSnippetData) => {
      const url = libraryRoutes.localSnippets;

      return apiClient.post(url, data, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Create Local Snippet',
        },
      });
    },
    getLocalSnippets: async (params?: GetSnippetsParams): Promise<SnippetsResponse> => {
      const queryParams = new URLSearchParams();

      if (params?.pageNum) queryParams.append('pageNum', params.pageNum.toString());
      if (params?.pageSize) queryParams.append('pageSize', params.pageSize.toString());
      if (params?.filters) {
        const apiFilters = transformFiltersToApi(params.filters);
        queryParams.append('filters', JSON.stringify(apiFilters));
      }
      if (params?.sort) {
        const apiField = transformSortFieldToApi(params.sort.field);
        queryParams.append('sort', JSON.stringify([[apiField, params.sort.direction]]));
      }

      const url = `${libraryRoutes.localSnippets}${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await apiClient.get<SnippetsResponse>(url, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Get Local Snippets',
        },
      });

      // Convert date strings to Date objects
      const parsed: SnippetsResponse = {
        ...response,
        snippets: response.snippets.map(s => ({
          ...s,
          id: s.token,
        })),
      };

      return parsed;
    },
    getLocalSnippet: (token: string): Promise<SnippetWithPublishedVersion> => {
      const url = libraryRoutes.localSnippet(token);

      return apiClient.get(url, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Get Local Snippet',
        },
      });
    },
    // not located here. see: `flow/api/localSnippets`
    // getLocalSnippetVersion() => { },
    listLocalSnippetVersions: (token: string): Promise<SnippetVersionsListResponse> => {
      const url = libraryRoutes.localSnippetVersions(token);

      return apiClient.get(url, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'List Local Snippet Versions',
        },
      });
    },
    updateLocalSnippetMetadata: (token: string, data: UpdateSnippetMetadataData) => {
      const url = libraryRoutes.localSnippetMetadata(token);

      return apiClient.patch(url, data, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Update Local Snippet Metadata',
        },
      });
    },
    archiveLocalSnippet: (token: string) => {
      const url = libraryRoutes.archiveLocalSnippet(token);

      return apiClient.patch(url, {}, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Archive Local Snippet',
        },
      });
    },
    unarchiveLocalSnippet: (token: string) => {
      const url = libraryRoutes.unarchiveLocalSnippet(token);

      return apiClient.patch(url, {}, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Unarchive Local Snippet',
        },
      });
    },
    publishLocalSnippet: (token: string, versionNumber: number) => {
      const url = libraryRoutes.publishLocalSnippet(token);

      return apiClient.patch(url, { version: versionNumber }, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Publish Local Snippet',
        },
      });
    },
    unpublishLocalSnippet: (token: string) => {
      const url = libraryRoutes.unpublishLocalSnippet(token);

      return apiClient.patch(url, {}, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Unpublish Local Snippet',
        },
      });
    },
    createLocalSnippetVersion: (data: CreateWorkflowVersionPayload) => {
      const url = libraryRoutes.localSnippetVersions(data.workflowVersion.token);

      return apiClient.post(url, data, {
        meta: {
          skipTransformRequestBody: true,
          skipTransformResponseBody: true,
        },
      });
    },
    listAllGlobalSnippets: async (
      params?: Omit<GetSnippetsParams, 'pageNum' | 'pageSize'>,
    ): Promise<SnippetMetadata[]> => (
      fetchAllPaginatedSnippets({
        fetcherFn: ({ pageNum, pageSize }) => (
          libraryAPI.getSnippets({ pageNum, pageSize, ...params })
        ),
      })
    ),
    listAllLocalAndGlobalSnippets: async (): Promise<SnippetMetadata[]> => {
      const [allLocalSnippets, allGlobalSnippets] = await Promise.all(
        [
          fetchAllPaginatedSnippets({ fetcherFn: libraryAPI.getLocalSnippets }),
          fetchAllPaginatedSnippets({ fetcherFn: libraryAPI.getSnippets }),
        ]);

      return [...allLocalSnippets, ...allGlobalSnippets];
    },
    getWorkflowNames: (): Promise<{ names: string[] }> => {
      const url = libraryRoutes.localSnippetWorkflowNames;

      return apiClient.get(url, {
        meta: {
          ...libraryApiMetaConfig,
          name: 'Get Workflow Names',
        },
      });
    },
  };
  return libraryAPI;
}
